/**
 * StudentBulkImport — CSV/Excel upload for bulk student import
 */
import React, { useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import {
  Upload, FileSpreadsheet, Download, CheckCircle2, XCircle, AlertTriangle,
  Loader2, ArrowRight, Users, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXPECTED_COLUMNS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name', required: true },
  { key: 'email', label: 'Email' },
  { key: 'date_of_birth', label: 'Date of Birth' },
  { key: 'gender', label: 'Gender' },
  { key: 'admission_number', label: 'Admission No.' },
  { key: 'class_name', label: 'Class Name' },
  { key: 'guardian_name', label: 'Guardian Name' },
  { key: 'guardian_phone', label: 'Guardian Phone' },
  { key: 'guardian_email', label: 'Guardian Email' },
  { key: 'guardian_relationship', label: 'Relationship' },
  { key: 'address', label: 'Address' },
  { key: 'previous_school', label: 'Previous School' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'religion', label: 'Religion' },
  { key: 'blood_group', label: 'Blood Group' },
];

export const StudentBulkImport = ({ tenantId, onComplete }) => {
  const [step, setStep] = useState('upload'); // upload, preview, importing, results
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileRef = useRef(null);

  const handleFileSelect = async (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload a .csv or .xlsx file');
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB.');
      return;
    }
    setFile(selectedFile);

    // Parse preview client-side for CSV
    if (ext === 'csv') {
      try {
        const text = await selectedFile.text();
        const lines = text.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase().replace(/ /g, '_'));
        const rows = lines.slice(1, 6).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          return headers.reduce((obj, h, i) => ({ ...obj, [h]: values[i] || '' }), {});
        });
        setPreview({ headers, rows, total: lines.length - 1 });
        setStep('preview');
      } catch (e) {
        toast.error('Failed to parse CSV');
      }
    } else {
      // For Excel, show file info and go to preview
      setPreview({ headers: ['Loading...'], rows: [], total: '?' });
      setStep('preview');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setStep('importing');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/students/bulk-import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Import failed');
      }

      const data = await res.json();
      setResults(data);
      setStep('results');

      if (data.created > 0) {
        toast.success(`${data.created} students imported successfully`);
        if (onComplete) onComplete();
      }
    } catch (e) {
      toast.error(e.message);
      setStep('preview');
    }
    setImporting(false);
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/school/tenants/${tenantId}/students/import-template`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error('Failed to download');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'student_import_template.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const reset = () => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setResults(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-4" data-testid="bulk-import">
      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Import Students from CSV / Excel</h3>
              <p className="text-xs text-slate-500 mt-0.5">Upload a spreadsheet with student data. Duplicates will be automatically skipped.</p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="h-4 w-4 mr-1" /> Download Template
            </Button>
          </div>

          <div
            className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-50'); }}
            onDragLeave={e => { e.preventDefault(); e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50'); }}
            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50'); handleFileSelect(e.dataTransfer.files[0]); }}
            data-testid="file-drop-zone"
          >
            <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-600">Drop your file here, or click to browse</p>
            <p className="text-xs text-slate-400 mt-1">Supported: .csv, .xlsx (max 10MB)</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={e => handleFileSelect(e.target.files?.[0])}
              data-testid="file-input"
            />
          </div>

          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <h4 className="text-xs font-semibold text-amber-800 mb-2">Required Columns</h4>
              <div className="flex flex-wrap gap-1.5">
                {EXPECTED_COLUMNS.map(c => (
                  <Badge key={c.key} className={c.required ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'} variant="outline">
                    {c.label} {c.required && '*'}
                  </Badge>
                ))}
              </div>
              <p className="text-[10px] text-amber-600 mt-2">* Required fields. Column names should match (case-insensitive, spaces become underscores).</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && preview && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-indigo-600" />
              <div>
                <p className="text-sm font-medium">{file?.name}</p>
                <p className="text-xs text-slate-400">{preview.total} rows detected {preview.headers.length > 0 && `| ${preview.headers.length} columns`}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={reset}>Change File</Button>
          </div>

          {/* Column mapping preview */}
          <Card>
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-slate-500 mb-2">Detected Columns</p>
              <div className="flex flex-wrap gap-1.5">
                {preview.headers.map(h => {
                  const expected = EXPECTED_COLUMNS.find(c => c.key === h);
                  return (
                    <Badge key={h} className={expected ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'} variant="outline">
                      {expected ? <CheckCircle2 className="h-3 w-3 mr-1" /> : null}
                      {h}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Data preview table */}
          {preview.rows.length > 0 && (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-10">#</TableHead>
                    {preview.headers.slice(0, 8).map(h => <TableHead key={h} className="text-xs">{h}</TableHead>)}
                    {preview.headers.length > 8 && <TableHead className="text-xs">...</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((row, i) => (
                    <TableRow key={`preview-${i}`}>
                      <TableCell className="text-xs text-slate-400">{i + 1}</TableCell>
                      {preview.headers.slice(0, 8).map(h => <TableCell key={h} className="text-xs">{row[h] || '—'}</TableCell>)}
                      {preview.headers.length > 8 && <TableCell className="text-xs text-slate-400">+{preview.headers.length - 8} cols</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-[10px] text-slate-400 p-2">Showing first {preview.rows.length} of {preview.total} rows</p>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={reset}>Cancel</Button>
            <Button onClick={handleImport} data-testid="start-import-btn">
              <Upload className="h-4 w-4 mr-1" /> Import {preview.total} Students
            </Button>
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {step === 'importing' && (
        <div className="text-center py-12">
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-indigo-500 mb-4" />
          <p className="text-sm font-medium text-slate-700">Importing students...</p>
          <p className="text-xs text-slate-400 mt-1">This may take a moment for large files</p>
        </div>
      )}

      {/* Step: Results */}
      {step === 'results' && results && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-slate-900">{results.total_rows}</p>
              <p className="text-xs text-slate-500">Total Rows</p>
            </CardContent></Card>
            <Card className="border-green-200"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{results.created}</p>
              <p className="text-xs text-green-600">Imported</p>
            </CardContent></Card>
            <Card className="border-amber-200"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{results.skipped}</p>
              <p className="text-xs text-amber-600">Skipped</p>
            </CardContent></Card>
            <Card className="border-red-200"><CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{results.errors}</p>
              <p className="text-xs text-red-600">Errors</p>
            </CardContent></Card>
          </div>

          {/* Skipped details */}
          {results.skipped_details?.length > 0 && (
            <Card className="border-amber-200">
              <CardContent className="p-3">
                <p className="text-xs font-semibold text-amber-700 mb-2"><AlertTriangle className="h-3.5 w-3.5 inline mr-1" /> Skipped Rows</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {results.skipped_details.map((s, i) => (
                    <p key={`skip-${i}`} className="text-xs text-amber-600">Row {s.row}: {s.reason}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error details */}
          {results.error_details?.length > 0 && (
            <Card className="border-red-200">
              <CardContent className="p-3">
                <p className="text-xs font-semibold text-red-700 mb-2"><XCircle className="h-3.5 w-3.5 inline mr-1" /> Errors</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {results.error_details.map((e, i) => (
                    <p key={`err-${i}`} className="text-xs text-red-600">Row {e.row} ({e.name}): {e.error}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Created summary */}
          {results.created_details?.length > 0 && (
            <Card className="border-green-200">
              <CardContent className="p-3">
                <p className="text-xs font-semibold text-green-700 mb-2"><CheckCircle2 className="h-3.5 w-3.5 inline mr-1" /> Successfully Imported ({results.created})</p>
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-xs">Name</TableHead>
                      <TableHead className="text-xs">Admission No.</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {results.created_details.slice(0, 20).map(s => (
                        <TableRow key={s.id}>
                          <TableCell className="text-xs">{s.name}</TableCell>
                          <TableCell className="text-xs font-mono">{s.admission_number}</TableCell>
                        </TableRow>
                      ))}
                      {results.created_details.length > 20 && (
                        <TableRow><TableCell colSpan={2} className="text-xs text-slate-400">...and {results.created_details.length - 20} more</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={reset} data-testid="import-another-btn">Import Another File</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentBulkImport;
